import { useCallback } from 'react';
import { addExportWatermark, removeExportWatermark } from '../utils/watermark';

/**
 * Hook for exporting the Fabric.js canvas as a PNG blob.
 * Adds the BLVX watermark during export, then removes it.
 */
export function useCanvasExport(fabricRef) {
  const exportAsBlob = useCallback(() => {
    return new Promise((resolve, reject) => {
      const canvas = fabricRef.current;
      if (!canvas) {
        reject(new Error('Canvas not initialized'));
        return;
      }

      // Deselect all objects
      canvas.discardActiveObject();
      canvas.renderAll();

      // Add watermark for export
      const watermark = addExportWatermark(canvas);

      // Export at full logical resolution (canvas is display-sized, so scale up)
      const zoom = canvas.getZoom();
      const dataUrl = canvas.toDataURL({
        format: 'png',
        quality: 1,
        multiplier: zoom > 0 ? 1 / zoom : 1,
      });

      // Remove watermark after capture
      removeExportWatermark(canvas, watermark);

      // Convert data URL to blob
      fetch(dataUrl)
        .then(res => res.blob())
        .then(blob => resolve(blob))
        .catch(reject);
    });
  }, [fabricRef]);

  const exportAsDataURL = useCallback(() => {
    const canvas = fabricRef.current;
    if (!canvas) return null;

    canvas.discardActiveObject();
    canvas.renderAll();

    const watermark = addExportWatermark(canvas);

    const zoom = canvas.getZoom();
    const dataUrl = canvas.toDataURL({
      format: 'png',
      quality: 1,
      multiplier: zoom > 0 ? 1 / zoom : 1,
    });

    removeExportWatermark(canvas, watermark);
    return dataUrl;
  }, [fabricRef]);

  return { exportAsBlob, exportAsDataURL };
}
